import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Edit2 } from "lucide-react";
import { notify } from "@/components/common/Notification";
import { PtsBillingService, PtsProjectService } from "@/services/APIs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import BillingInvoiceFilters, {
  type BillingInvoiceFilterValues,
} from "./BillingInvoiceFilters";
import BillingInvoicePagination from "./BillingInvoicePagination";
import PaymentStatusEditModal from "./PaymentStatusEditModal";
import PaymentStatusSelect from "./PaymentStatusSelect";
import {
  BILLING_DEFAULT_PAGE_SIZE,
  type PaginatedResult,
} from "./billingPaginationUtils";
import { resolveProjectLabel } from "./billingListUtils";
import {
  PAYMENT_STATUS_QUERY_KEY,
  buildPaymentStatusOnlyPayload,
  buildUpdatePaymentStatusPayload,
  formatDisplayDate,
} from "./paymentStatusUtils";
import {
  PaymentHistoryEntry,
  PaymentStatusRecord,
  PaymentTrackingStatus,
  UpdatePaymentStatusPayload,
  PAYMENT_TRACKING_STATUS_OPTIONS,
} from "./types";
import { inr } from "./utils";

const PAYMENT_STATUS_COLUMN_COUNT = 15;

function PaymentHistoryExpandedDetails({
  record,
}: {
  record: PaymentStatusRecord;
}) {
  const history = record.paymentHistory ?? [];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="bg-muted/30 border-b border-gray-200 px-4 py-3">
        <p className="text-primary text-sm font-bold uppercase tracking-wide">
          Payment History
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          Invoice #{record.id} · {history.length} payment
          {history.length === 1 ? "" : "s"} recorded
        </p>
      </div>

      <div className="p-4">
        <div className="overflow-hidden rounded-md border border-gray-200">
          <Table className="[&_tbody_tr]:border-0">
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60 border-b border-gray-300">
                <TableHead className="text-primary w-10 px-3">#</TableHead>
                <TableHead className="text-primary px-3 text-center">
                  Received Amount
                </TableHead>
                <TableHead className="text-primary px-3 text-center">
                  Received Date
                </TableHead>
                <TableHead className="text-primary px-3 text-center">
                  Pending After
                </TableHead>
                <TableHead className="text-primary px-3">Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    No payment history recorded yet
                  </TableCell>
                </TableRow>
              ) : (
                history.map((entry: PaymentHistoryEntry, index) => (
                  <TableRow
                    key={entry.id ?? index}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50"
                  >
                    <TableCell className="w-10 px-3 text-xs font-semibold text-gray-500">
                      {index + 1}
                    </TableCell>
                    <TableCell className="px-3 text-center text-sm font-medium text-emerald-600">
                      {inr(entry.receivedAmount)}
                    </TableCell>
                    <TableCell className="px-3 text-center text-xs text-gray-600">
                      {entry.receivedDate
                        ? formatDisplayDate(entry.receivedDate)
                        : "—"}
                    </TableCell>
                    <TableCell className="px-3 text-center text-sm font-medium text-amber-600">
                      {entry.pendingAmount > 0 ? inr(entry.pendingAmount) : "—"}
                    </TableCell>
                    <TableCell className="px-3 text-xs text-gray-700">
                      {entry.remarks?.trim() || "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const PaymentStatusTab = () => {
  const queryClient = useQueryClient();
  const [filterValues, setFilterValues] = useState<BillingInvoiceFilterValues>({
    startDate: "",
    endDate: "",
    projectId: undefined,
    status: undefined,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(BILLING_DEFAULT_PAGE_SIZE);
  const { startDate, endDate, projectId, status } = filterValues;

  const handleFilterChange = (values: BillingInvoiceFilterValues) => {
    setFilterValues(values);
    setPage(1);
  };

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  const paymentStatusQueryKey = [
    ...PAYMENT_STATUS_QUERY_KEY,
    startDate,
    endDate,
    projectId,
    status,
    page,
    pageSize,
  ] as const;
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] =
    useState<PaymentStatusRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [updatingRecordId, setUpdatingRecordId] = useState<string | null>(null);

  const {
    data: paymentStatusData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: paymentStatusQueryKey,
    queryFn: () =>
      PtsBillingService.getPaymentStatusRecords(
        startDate,
        endDate,
        projectId,
        page,
        pageSize,
        status
      ),
  });

  const records = paymentStatusData?.items ?? [];
  const totalCount = paymentStatusData?.count ?? 0;

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = projectOptions.map(option => ({
    value: option.value,
    label: option.label,
  }));

  const updateMutation = useMutation({
    mutationFn: ({
      recordId,
      payload,
    }: {
      recordId: string;
      payload: UpdatePaymentStatusPayload;
    }) => PtsBillingService.updatePaymentStatus(recordId, payload),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: PAYMENT_STATUS_QUERY_KEY });
    },
  });

  const updateRecordInCache = (
    recordId: string,
    updater: (record: PaymentStatusRecord) => PaymentStatusRecord
  ) => {
    queryClient.setQueryData<PaginatedResult<PaymentStatusRecord>>(
      paymentStatusQueryKey,
      current => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          items: current.items.map(record =>
            record.recordId === recordId ? updater(record) : record
          ),
        };
      }
    );
  };

  const toggleExpanded = (recordId: string) => {
    setExpandedRecordId(current => (current === recordId ? null : recordId));
  };

  const handleStatusChange = async (
    record: PaymentStatusRecord,
    newStatus: PaymentTrackingStatus
  ) => {
    if (newStatus === record.status) {
      return;
    }

    const previousStatus = record.status;
    const payload = buildPaymentStatusOnlyPayload(newStatus);

    updateRecordInCache(record.recordId, current => ({
      ...current,
      status: newStatus,
    }));

    setUpdatingRecordId(record.recordId);
    const response = await updateMutation.mutateAsync({
      recordId: record.recordId,
      payload,
    });
    setUpdatingRecordId(null);

    if (response === undefined) {
      updateRecordInCache(record.recordId, current => ({
        ...current,
        status: previousStatus,
      }));
      return;
    }

    notify.success(`Payment status updated to ${newStatus}`);
  };

  const handleEditClick = (record: PaymentStatusRecord) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleSaveRecord = async (updatedRecord: PaymentStatusRecord) => {
    const payload = buildUpdatePaymentStatusPayload(
      updatedRecord.status,
      updatedRecord.receivedItems ?? []
    );

    updateRecordInCache(updatedRecord.recordId, () => updatedRecord);

    const response = await updateMutation.mutateAsync({
      recordId: updatedRecord.recordId,
      payload,
    });

    if (response === undefined) {
      await queryClient.refetchQueries({ queryKey: PAYMENT_STATUS_QUERY_KEY });
      return;
    }

    notify.success(`Payment details updated for ${updatedRecord.id}`);
    setModalOpen(false);
    setEditingRecord(null);
  };

  const isSaving = updateMutation.isPending;

  return (
    <>
      <div className="space-y-6">
        <BillingInvoiceFilters
          idPrefix="paymentStatus"
          values={filterValues}
          onChange={handleFilterChange}
          statusOptions={PAYMENT_TRACKING_STATUS_OPTIONS}
        />

        <Card className="border border-gray-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Payment Status</CardTitle>
            <CardDescription>
              Track received and pending payments. Click a row to view payment
              history.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto px-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead className="text-primary font-semibold">
                      Invoice #
                    </TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">Received</TableHead>
                    <TableHead className="text-center">Received Date</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    {/* <TableHead className="text-center">Next Date</TableHead> */}
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={PAYMENT_STATUS_COLUMN_COUNT}
                        className="text-muted-foreground py-8 text-center text-sm"
                      >
                        Loading payment status...
                      </TableCell>
                    </TableRow>
                  ) : isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={PAYMENT_STATUS_COLUMN_COUNT}
                        className="text-muted-foreground py-8 text-center text-sm"
                      >
                        Failed to load payment status
                      </TableCell>
                    </TableRow>
                  ) : records.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={PAYMENT_STATUS_COLUMN_COUNT}
                        className="text-muted-foreground py-8 text-center text-sm"
                      >
                        No payment records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map(record => {
                      const isExpanded = expandedRecordId === record.recordId;
                      const paymentCount = record.paymentHistory?.length ?? 0;

                      return (
                        <Fragment key={record.recordId}>
                          <TableRow
                            className={`cursor-pointer ${
                              isExpanded
                                ? "bg-primary/5 hover:bg-primary/5"
                                : "hover:bg-muted/40"
                            }`}
                            onClick={() => toggleExpanded(record.recordId)}
                          >
                            <TableCell className="w-10 px-2">
                              {isExpanded ? (
                                <ChevronDown className="text-primary h-4 w-4" />
                              ) : (
                                <ChevronRight className="text-muted-foreground h-4 w-4" />
                              )}
                            </TableCell>
                            <TableCell className="text-primary font-semibold">
                              <div>{record.id}</div>
                              {paymentCount > 0 ? (
                                <div className="text-muted-foreground mt-0.5 text-xs font-normal">
                                  {paymentCount} payment
                                  {paymentCount > 1 ? "s" : ""}
                                </div>
                              ) : null}
                            </TableCell>
                            <TableCell>{record.client}</TableCell>
                            <TableCell>
                              {resolveProjectLabel(
                                record.project,
                                projectSelectOptions
                              )}
                            </TableCell>
                            <TableCell>{record.frequency ?? "—"}</TableCell>
                            <TableCell className="text-right">
                              {inr(record.amount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {inr(record.gst)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {inr(record.total)}
                            </TableCell>
                            <TableCell
                              onClick={event => event.stopPropagation()}
                            >
                              <PaymentStatusSelect
                                value={record.status}
                                disabled={
                                  isSaving &&
                                  updatingRecordId === record.recordId
                                }
                                onValueChange={newStatus =>
                                  handleStatusChange(record, newStatus)
                                }
                              />
                            </TableCell>
                            <TableCell>{record.invoiceDate ?? "—"}</TableCell>
                            <TableCell className="text-center text-green-600">
                              {record.received !== null
                                ? inr(record.received ?? 0)
                                : "—"}
                            </TableCell>
                            <TableCell className="text-center">
                              {record.receivedDate ?? "—"}
                            </TableCell>
                            <TableCell className="text-center text-red-600">
                              {record.pending !== null
                                ? inr(record.pending ?? 0)
                                : "—"}
                            </TableCell>
                            {/* <TableCell className="text-center">
                            {record.nextDate ?? "—"}
                          </TableCell> */}
                            <TableCell
                              className="text-center"
                              onClick={event => event.stopPropagation()}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-primary hover:text-primary gap-1.5"
                                disabled={isSaving}
                                onClick={() => handleEditClick(record)}
                              >
                                <Edit2 className="h-4 w-4" />
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                          {isExpanded ? (
                            <TableRow className="bg-primary/5 hover:bg-primary/5">
                              <TableCell
                                colSpan={PAYMENT_STATUS_COLUMN_COUNT}
                                className="bg-muted/10 px-3 py-3 sm:px-4"
                              >
                                <PaymentHistoryExpandedDetails
                                  record={record}
                                />
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            <BillingInvoicePagination
              current={page}
              pageSize={pageSize}
              total={totalCount}
              onChange={handlePaginationChange}
              disabled={isLoading}
            />
          </CardContent>
        </Card>
      </div>

      <PaymentStatusEditModal
        open={modalOpen}
        record={editingRecord}
        isSubmitting={isSaving}
        onOpenChange={open => {
          setModalOpen(open);
          if (!open) {
            setEditingRecord(null);
          }
        }}
        onSave={handleSaveRecord}
      />
    </>
  );
};

export default PaymentStatusTab;
